import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, ExternalLink, Loader2 } from 'lucide-react';
import EmployeeLayout from '../components/layout/EmployeeLayout';
import SupervisorLayout from '../components/layout/SupervisorLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MandatoryTrainingPage (Employee + Supervisor view)
 *
 * Read-only list of the training courses added by Admin. Each row has a
 * clear "Open Course" button that opens the link in a new tab.
 */
export default function MandatoryTrainingPage({ role = 'employee' }) {
  const Layout = role === 'supervisor' ? SupervisorLayout : EmployeeLayout;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/mandatory-trainings`);
        setRows(data || []);
      } catch (err) {
        toast.error('Failed to load trainings.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="employee-mandatory-training-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-yellow-500" />
          Mandatory Training
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Complete every course below. Click <span className="font-medium text-foreground">Open Course</span> to launch the training in a new tab.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No training courses have been added yet. Please check back later.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="training-grid">
          {rows.map((row) => (
            <Card key={row.id} data-testid={`training-card-${row.id}`}>
              <CardHeader>
                <CardTitle className="text-base flex items-start gap-2">
                  <BookOpen className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                  <span className="break-words">{row.name}</span>
                </CardTitle>
                <CardDescription className="text-xs break-all">
                  {row.url}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button
                  className="w-full"
                  onClick={() => window.open(row.url, '_blank', 'noopener,noreferrer')}
                  data-testid={`open-training-${row.id}`}
                >
                  <ExternalLink className="w-4 h-4 mr-2" /> Open Course
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </Layout>
  );
}
